import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import CommonTable from '../../components/commonComponent/CommanTable';
import UserView from './UserView';
import CommonModal from '../../components/commonComponent/CommonModal';
import { FaEye, FaEdit, FaPlus } from 'react-icons/fa';
import axios from '../../utils/axiosInstance';
import { toastSuccess, toastError } from '../../utils/alertHelper';

// User Table Columns
const userFields = [
  { key: 'srNo', label: 'Sr.no' },
  { key: 'userId', label: 'User ID' },
  { key: 'name', label: 'Name' },
  { key: 'emailId', label: 'Email id' },
  { key: 'role', label: 'Role' },
  { key: 'status', label: 'Status' },
  { key: 'actions', label: 'Action' },
];

// Role Table Columns
const roleFields = [
  { key: 'srNo', label: 'Sr.no' }, 
  { key: 'roleName', label: 'Role' },
  { key: 'description', label: 'Description' },
  { key: 'actions', label: 'Action' },
];

function UserRole() {
  const [activeTab, setActiveTab] = useState('user');
  const [showUserModal, setShowUserModal] = useState(false);
  const [userModalData, setUserModalData] = useState({
    firstName: '',
    lastName: '',
    emailId: '',
    role: '',
    status: 'Active',
    note: '',
    userType: localStorage.getItem("userType") || 'Admin',
  });
  const [userErrors, setUserErrors] = useState({});
  const [userSubmitting, setUserSubmitting] = useState(false);

  // Table Data
  const [users, setUsers] = useState([]);
  const [roles, setRoles] = useState([]);
  const [userSearchTerm, setUserSearchTerm] = useState('');
  const [roleSearchTerm, setRoleSearchTerm] = useState('');
  const [viewUserModal, setViewUserModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [roleOptions, setRoleOptions] = useState([]);
  const [isEditing, setIsEditing] = useState(false);
  
  const navigate = useNavigate();

  useEffect(() => {
    fetchRoles();
    fetchUsers();
  }, []);

  // Fetch roles from API
  async function fetchRoles() {
    try {
      let url = `/admin/api/role`;
      const res = await axios.get(url);
      const data = res.data;
      if (data.Status === 1 && Array.isArray(data.roles)) {
        setRoles(data.roles.map((r, idx) => ({
          id: r._id,
          roleName: r.name,
          description: r.description || '',
          createdAt: r.createdAt,
          updatedAt: r.updatedAt,
        })));
        setRoleOptions(data.roles.map(r => ({ 
          value: r._id, 
          label: r.name, 
          description: r.description || '' 
        })));
      }
    } catch (err) {
      console.error('Error fetching roles:', err);
      toastError('Failed to load roles');
    }
  }

  // Fetch users from API
  async function fetchUsers() {
    try {
      const userType = localStorage.getItem('userType') || 'Admin';
      const url = `/api/user?userType=${userType}`;
      const res = await axios.get(url);
      const data = res.data || {};
      
      const usersList = Array.isArray(data.users)
        ? data.users
        : Array.isArray(data.data && data.data.users)
        ? data.data.users
        : [];
        
      if ((data.Status === 1 || data.Status === '1') && usersList.length) {
        setUsers(usersList.map(u => ({
          id: u._id,
          userId: u.memberId || u.memberId || '',
          firstName: u.firstName || '',
          lastName: u.lastName || '',
          name: ((u.firstName || '') + ' ' + (u.lastName || '')).trim() || u.name || '',
          emailId: u.email || u.emailId || '',
          roleId: (u.role && (u.role._id || u.role)) || '',
          roleName: (u.role && (u.role.name || '')) || (typeof u.role === 'string' ? u.role : ''),
          status: u.userStatus || u.accountStatus || u.status || '',
          note: u.note || '',
          userType: u.userType || '',
        })));
      }
    } catch (err) {
      console.error('Error fetching users:', err);
      toastError('Failed to load users');
    }
  }

  // User Table Data
  const userTableData = users
    .filter(u =>
      u.name.toLowerCase().includes(userSearchTerm.toLowerCase()) ||
      u.userId.toLowerCase().includes(userSearchTerm.toLowerCase()) ||
      u.emailId.toLowerCase().includes(userSearchTerm.toLowerCase())
    )
    .map((u, idx) => ({
      ...u,
      srNo: idx + 1,
      status: (
        <span
          className={`badge px-3 py-2 rounded-pill`}
          style={{
            backgroundColor: u.status === 'Active' ? '#DCF1E4' : '#252C3226',
            fontSize: '0.875rem',
            color: u.status === 'Active' ? '#16A34A' : '#252C32',
            fontWeight: 400,
          }}
        >
          {u.status}
        </span>
      ),
      role: u.roleName || '',
      actions: (
        <div>
          <button
            className="btn btn-sm py-0 px-1 fs-6 lh-sm shadow-none border-0 text-primary-custom"
            title="View"
            onClick={() => {
              setSelectedUser(u);
              setViewUserModal(true);
            }}
          >
            <FaEye size={14} />
          </button>
          <button
            className="btn btn-sm py-0 px-1 fs-6 lh-sm shadow-none border-0 text-primary-custom"
            title="Edit"
            onClick={() => {
              // Set edit mode and populate modal data
              setIsEditing(true);
              setUserModalData({
                id: u.id,
                firstName: u.firstName || '',
                lastName: u.lastName || '',
                emailId: u.emailId || '',
                role: u.roleId || u.role || '',
                status: u.status || 'Active',
                note: u.note || '',
                userType: u.userType || localStorage.getItem("userType") || 'Admin',
              });
              setShowUserModal(true);
            }}
          >
            <FaEdit size={14} />
          </button>
        </div>
      ),
    }));
   
  // Role Table Data
  const roleTableData = roles
    .filter(r =>
      r.roleName.toLowerCase().includes(roleSearchTerm.toLowerCase()) ||
      (r.description && r.description.toLowerCase().includes(roleSearchTerm.toLowerCase()))
    )
    .map((r, idx) => ({
      ...r,
      srNo: idx + 1,
      actions: (
        <div>
          <button
            className="btn btn-sm py-0 px-1 fs-6 lh-sm shadow-none border-0 text-primary-custom"
            title="Edit"
            onClick={() => {
              navigate(`/admin/userRole/edit/${r.id}`);
            }}
          >
            <FaEdit size={14} />
          </button>
        </div>
      ),
    }));

  // User Modal Submit - Fixed version
  const handleUserSubmit = async () => {
    const errors = {};
    ['role','firstName','lastName','status','emailId'].forEach(f => {
      if (!userModalData[f]) errors[f] = "Required";
    });
    setUserErrors(errors);
    if (Object.keys(errors).length > 0) return;
    setUserSubmitting(true);

    const userType = localStorage.getItem("userType") || 'Admin';

    // Prepare payload - use the correct field names that your backend expects
    const payload = {
      firstName: String(userModalData.firstName || '').trim(),
      lastName: String(userModalData.lastName || '').trim(),
      email: String(userModalData.emailId || '').trim(),
      role: String(userModalData.role || '').trim(),
      // Try different status field names based on what your API expects
      status: String(userModalData.status || 'Active').trim(),
      userStatus: String(userModalData.status || 'Active').trim(),
      note: String(userModalData.note || '').trim(),
      userType: String(userType).trim(),
    };

    console.log('User save payload:', payload);

    try {
      let res;
      let url;
      
      if (isEditing && userModalData.id) {
        // For update - check if your API uses PATCH or PUT
        url = `/api/user/${userModalData.id}`;
        console.log('Updating user at:', url);
        res = await axios.put(url, payload, {
          headers: { 'Content-Type': 'application/json' }
        });
      } else {
        // For create
        url = '/api/user/create';
        console.log('Creating user at:', url);
        res = await axios.post(url, payload, {
          headers: { 'Content-Type': 'application/json' }
        });
      }
      
      const data = res.data;
      console.log('API Response:', data);
      
      if (data.Status === 1 || data.status === 'success') {
        toastSuccess(data.Message || (isEditing ? 'User updated successfully!' : 'User created successfully!'));
        
        // Refresh user list
        await fetchUsers();
        
        // Reset modal
        setShowUserModal(false);
        setIsEditing(false);
        setUserModalData({
          firstName: '',
          lastName: '',
          emailId: '',
          role: '',
          status: 'Active',
          note: '',
          userType: localStorage.getItem("userType") || 'Admin',
        });
        setUserErrors({});
      } else {
        const errorMessage = data.Message || (isEditing ? "Failed to update user" : "Failed to create user");
        console.error('User save failed:', errorMessage);
        toastError(errorMessage);
        setUserErrors({ api: errorMessage });
      }
    } catch (err) {
      console.error('User save error:', err);
      console.error('Error response:', err.response);
      
      // More detailed error handling
      let errorMessage = 'An error occurred';
      if (err.response) {
        errorMessage = err.response.data?.Message || 
                      err.response.data?.error || 
                      err.response.data?.message || 
                      `Server error: ${err.response.status}`;
      } else if (err.message) {
        errorMessage = err.message;
      }
      
      toastError(errorMessage);
      setUserErrors({ api: errorMessage });
    }
    setUserSubmitting(false);
  };

  // Handle modal cancel
  const handleModalCancel = () => {
    setShowUserModal(false);
    setIsEditing(false);
    setUserModalData({
      firstName: '',
      lastName: '',
      emailId: '',
      role: '',
      status: 'Active',
      note: '',
      userType: localStorage.getItem("userType") || 'Admin',
    });
    setUserErrors({});
  };

  return (
    <div className="container-fluid mt-4">
      <div className="d-flex justify-content-between align-items-center mb-4 mt-4">
        <h2 className="h2 mb-0">User & Role</h2>
        <div className="d-flex gap-2 align-items-center">
          {activeTab === 'user' ? (
            <button className="btn btn-primary" onClick={() => { 
              setIsEditing(false);
              const currentUserType = localStorage.getItem("userType") || 'Admin';
              setUserModalData({
                firstName: '',
                lastName: '',
                emailId: '',
                role: '',
                status: 'Active',
                note: '',
                userType: currentUserType,
              }); 
              setShowUserModal(true); 
            }}>
              <FaPlus className="me-1" /> Create New User
            </button>
          ) : (
            <button className="btn btn-primary" onClick={() => navigate('/admin/userRole/create')}>
              <FaPlus className="me-1" /> Create New Role
            </button>
          )}
        </div>
      </div>

      {/* Tabs */}
      <div className="mb-3">
        <button
          className={`btn px-3 py-2 me-2 ${
            activeTab === 'user' ? "btn-primary" : "btn-light"
          }`}
          style={{ borderRadius: "6px", fontWeight: 500 }}
          onClick={() => setActiveTab('user')}
        >
          User {activeTab === 'user' && `(${users.length})`}
        </button>
        <button
          className={`btn px-3 py-2 me-2 ${
            activeTab === 'role' ? "btn-primary" : "btn-light"
          }`}
          style={{ borderRadius: "6px", fontWeight: 500 }}
          onClick={() => setActiveTab('role')}
        >
          Role {activeTab === 'role' && `(${roles.length})`}
        </button>
      </div>

      {activeTab === 'user' ? (
        <>
          {/* Search Bar */}
          <div className="row mb-4">
            <div className="col-md-6"></div>
            <div className="col-md-6 d-flex justify-content-end">
              <div className="input-group" style={{ maxWidth: 280, borderRadius: '6px', overflow: 'hidden' }}>
                <span className="input-group-text bg-white border-end-0" style={{ borderRadius: '6px 0 0 6px' }}>
                  <i className="fas fa-search text-muted"></i>
                </span>
                <input
                  type="text"
                  className="form-control border-start-0"
                  placeholder="Type to search..."
                  value={userSearchTerm}
                  onChange={e => setUserSearchTerm(e.target.value)}
                  style={{ boxShadow: 'none', borderRadius: '0 6px 6px 0' }}
                />
              </div>
            </div>
          </div>
          {/* User Table */}
          <CommonTable
            columns={userFields}
            data={userTableData}
            itemsPerPageOptions={[10, 15, 25, 50]}
            defaultItemsPerPage={15}
          />
          {/* User Modal */}
          <CommonModal
            visible={showUserModal}
            title={isEditing ? "Edit User" : "Add New User"}
            fields={[
              { name: 'firstName', label: 'First Name', type: 'text', placeholder: 'First Name' },
              { name: 'lastName', label: 'Last Name', type: 'text', placeholder: 'Last Name' },
              { name: 'emailId', label: 'Email ID', type: 'text', placeholder: 'Email ID', disabled: isEditing },
              { name: 'role', label: 'Role', type: 'select', options: roleOptions, placeholder: 'Select Role' },
              { name: 'status', label: 'Status', type: 'select', options: [
                  { value: 'Active', label: 'Active' },
                  { value: 'Inactive', label: 'Inactive' }
                ], placeholder: 'Select Status'
              },
              { name: 'note', label: 'Note', type: 'textarea', placeholder: 'Note', maxLength: 500 },
            ]}
            values={userModalData}
            onChange={setUserModalData}
            onCancel={handleModalCancel}
            onSubmit={handleUserSubmit}
            submitLabel={userSubmitting ? (isEditing ? "Updating..." : "Saving...") : (isEditing ? "Update" : "Save")}
            cancelLabel="Cancel"
            requiredFields={['role','firstName','lastName','status','emailId']}
            errors={userErrors}
            disabled={userSubmitting}
          />

          <UserView
            visible={viewUserModal}
            user={selectedUser}
            onClose={() => setViewUserModal(false)}
          />
        </>
      ) : (
        <>
          {/* Search Bar */}
          <div className="row mb-4">
            <div className="col-md-6"></div>
            <div className="col-md-6 d-flex justify-content-end">
              <div className="input-group" style={{ maxWidth: 280, borderRadius: '6px', overflow: 'hidden' }}>
                <span className="input-group-text bg-white border-end-0" style={{ borderRadius: '6px 0 0 6px' }}>
                  <i className="fas fa-search text-muted"></i>
                </span>
                <input
                  type="text"
                  className="form-control border-start-0"
                  placeholder="Type to search..."
                  value={roleSearchTerm}
                  onChange={e => setRoleSearchTerm(e.target.value)}
                  style={{ boxShadow: 'none', borderRadius: '0 6px 6px 0' }}
                />
              </div>
            </div>
          </div>
          {/* Role Table */}
          <CommonTable
            columns={roleFields}
            data={roleTableData}
            itemsPerPageOptions={[10, 15, 25, 50]}
            defaultItemsPerPage={15}
          />
        </>
      )}
    </div>
  );
}

export default UserRole;